/**
 * /superadmin/orbitprint/orgs — the Organizations shelf (index).
 * Answers: "who are the tenants, and which need me today?"
 *   health strip (Home, as a strip — never its own page) · attention strip · search + filter in the
 *   URL (?q= &filter=) · the table: 7 columns, everything else lives on the detail page.
 * Architecture: docs/PLATFORM-ADMIN-ARCHITECTURE.md.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import PlatformAdminShell, { HOUSE, Chip, Badge, RouteState, card, sectionTitle, useSuperAdminGate } from '../PlatformAdminShell';
import { orbitAdminApi, SHELVES, PLAN_CONFIG, STATUS_CONFIG, ATTENTION, FILTERS, dateInEffect, peso, errorKind } from './orbitAdmin';

function Stat({ label, value, sub, color = HOUSE.text, testId }) {
  return (
    <div style={{ ...card, padding: '12px 14px', minWidth: 0 }} data-testid={testId}>
      <p className="text-xs uppercase tracking-wide" style={{ color: HOUSE.dim }}>{label}</p>
      <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 24, fontWeight: 700, color, lineHeight: 1.2, marginTop: 2 }}>{value}</p>
      {sub && <p className="text-xs" style={{ color: HOUSE.mute }}>{sub}</p>}
    </div>
  );
}

export function PlanBadge({ plan }) {
  const c = PLAN_CONFIG[plan] || { label: plan, color: HOUSE.mute };
  return <Badge color={c.color}>{c.label}</Badge>;
}

export function StatusBadge({ status }) {
  const c = STATUS_CONFIG[status] || { label: status, color: HOUSE.mute };
  return <Badge color={c.color}>{c.label}</Badge>;
}

export function AttentionChips({ flags, small }) {
  if (!flags || flags.length === 0) return <span style={{ color: HOUSE.faint, fontSize: 12 }}>—</span>;
  return (
    <div className="flex gap-1 flex-wrap">
      {flags.map(f => {
        const a = ATTENTION[f] || { label: f, color: HOUSE.mute };
        return <Badge key={f} color={a.color}>{a.label}</Badge>;
      })}
    </div>
  );
}

export default function OrbitAdminOrgsPage() {
  const ready = useSuperAdminGate();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filter = params.get('filter') || 'all';
  const q = params.get('q') || '';
  const [qInput, setQInput] = useState(q);
  const api = useMemo(() => orbitAdminApi(), []);

  const [stats, setStats] = useState(null);
  const [data, setData] = useState(null);      // {orgs, attention}
  const [state, setState] = useState('loading');

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value && value !== 'all') next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const load = useCallback(() => {
    setState('loading');
    Promise.all([api.get('/stats'), api.get('/orgs', { params: { q, filter } })])
      .then(([s, o]) => { setStats(s.data); setData(o.data); setState('ok'); })
      .catch(e => { setState(errorKind(e)); if (e.response?.status === 403) toast.error('Super admin access required'); });
  }, [api, q, filter]);

  useEffect(() => { if (ready) load(); }, [ready, load]);
  // search: commit to the URL after a short pause so the back button gets one entry per search, not per keystroke
  useEffect(() => {
    const t = setTimeout(() => { if (qInput !== q) setParam('q', qInput.trim()); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);

  const orgs = data?.orgs || [];
  const att = data?.attention || {};
  const strip = [
    { k: 'in_grace', n: att.in_grace },
    { k: 'expiring_soon', n: att.expiring },
    { k: 'over_cap', n: att.over_cap },
    { k: 'suspended', n: att.suspended },
    { k: 'never_expires', n: att.never_expires },
  ].filter(x => x.n > 0);

  return (
    <PlatformAdminShell product="orbitprint" title="Organizations" shelves={SHELVES} active="orgs"
      headerRight={
        <button onClick={load} data-testid="orbit-admin-refresh"
          style={{ background: HOUSE.card, border: `1px solid ${HOUSE.line2}`, color: HOUSE.mute, borderRadius: 8, padding: '8px 14px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      }>
      {!ready ? null : state === 'denied' ? (
        <RouteState kind="denied" message="Super admin access required. Sign in to the POS as the platform owner." />
      ) : state === 'error' ? (
        <RouteState kind="error" message="Could not load OrbitPrint organizations." onRetry={load} />
      ) : (
        <>
          {/* Home = a strip, never a page */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" style={{ padding: '16px 16px 0 16px' }} data-testid="orbit-admin-stats">
              <Stat label="Organizations" value={stats.orgs} sub={`${stats.by_plan?.premium || 0} Premium · ${stats.by_plan?.enterprise || 0} Enterprise`} testId="orbit-admin-stat-orgs" />
              <Stat label="Users" value={stats.users} />
              <Stat label="Locations" value={stats.locations} sub={`${stats.hubs_online} HUB${stats.hubs_online === 1 ? '' : 's'} online now`} color={stats.hubs_online > 0 ? HOUSE.ok : HOUSE.text} />
              <Stat label="Documents this month" value={stats.documents_this_month} sub={stats.month} />
              <Stat label="Monthly revenue (est.)" value={peso(stats.mrr_estimate)} sub="active + grace paid plans" color={stats.mrr_estimate > 0 ? HOUSE.ok : HOUSE.text} testId="orbit-admin-stat-mrr" />
            </div>
          )}

          {/* Attention strip — what needs a look today */}
          {strip.length > 0 && (
            <div data-testid="orbit-admin-attention-strip" style={{ margin: '16px 16px 0 16px', ...card, padding: '12px 16px' }}>
              <p className="text-xs uppercase tracking-wider mb-2" style={{ color: HOUSE.warn, fontWeight: 800 }}>Needs attention</p>
              <div className="flex gap-2 flex-wrap">
                {strip.map(x => {
                  const a = ATTENTION[x.k];
                  return (
                    <Chip key={x.k} color={a.color} active={filter === a.filter && a.filter !== 'all'} testId={`orbit-admin-attention-${x.k}`}
                      onClick={() => setParam('filter', filter === a.filter ? 'all' : a.filter)}>
                      {a.label} ({x.n})
                    </Chip>
                  );
                })}
              </div>
            </div>
          )}

          {/* Search + filter tabs — both live in the URL */}
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${HOUSE.line}`, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <div className="flex items-center gap-2" style={{ background: HOUSE.card, border: `1px solid ${HOUSE.line2}`, borderRadius: 8, padding: '6px 10px', minWidth: 220 }}>
              <Search className="h-3.5 w-3.5" style={{ color: HOUSE.dim }} />
              <input value={qInput} onChange={e => setQInput(e.target.value)} placeholder="Name, owner email, id" data-testid="orbit-admin-search"
                style={{ background: 'transparent', border: 'none', outline: 'none', color: HOUSE.text, fontSize: 13, width: '100%' }} />
            </div>
            <div className="flex gap-2 flex-wrap">
              {FILTERS.map(f => (
                <button key={f.k} onClick={() => setParam('filter', f.k)} data-testid={`orbit-admin-filter-${f.k}`}
                  style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                    background: filter === f.k ? HOUSE.accent : HOUSE.card, color: filter === f.k ? '#fff' : HOUSE.dim,
                    border: `1px solid ${filter === f.k ? HOUSE.accent : HOUSE.line2}` }}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* The table — 7 columns; the rest is on the detail page */}
          <div style={{ padding: '16px' }}>
            {state === 'loading' ? <RouteState kind="loading" /> : orgs.length === 0 ? (
              <RouteState kind="empty" message={q || filter !== 'all' ? 'No organizations match.' : 'No OrbitPrint organizations yet.'} />
            ) : (
              <div style={{ ...card, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} data-testid="orbit-admin-orgs-table">
                  <thead>
                    <tr style={{ color: HOUSE.dim, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, textAlign: 'left' }}>
                      {['Organization', 'Plan', 'Status', 'Locations', 'Members', 'Docs / mo', 'Attention'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', borderBottom: `1px solid ${HOUSE.line2}`, fontWeight: 800, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {orgs.map(o => {
                      const d = dateInEffect(o);
                      return (
                        <tr key={o.id} onClick={() => navigate(`/superadmin/orbitprint/orgs/${o.id}`)} data-testid={`orbit-admin-org-${o.id}`}
                          style={{ cursor: 'pointer', borderBottom: `1px solid ${HOUSE.line}` }}
                          onMouseEnter={e => (e.currentTarget.style.background = '#181818')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                          <td style={{ padding: '10px 14px', minWidth: 200 }}>
                            <p style={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, textTransform: 'uppercase', color: HOUSE.text }}>{o.name}</p>
                            <p className="text-xs" style={{ color: HOUSE.mute }}>{o.owner?.email || '—'}</p>
                          </td>
                          <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                            <PlanBadge plan={o.plan} />
                            <p className="text-xs mt-1" style={{ color: HOUSE.mute }}>
                              {[
                                o.plan === 'free' || o.plan === 'founder' ? null : o.monthly_rate != null ? `${peso(o.monthly_rate)}/mo · negotiated` : `${peso(o.monthly_estimate)}/mo`,
                                o.extra_location_slots > 0 ? `+${o.extra_location_slots} slot${o.extra_location_slots === 1 ? '' : 's'}` : null,
                              ].filter(Boolean).join(' · ')}
                            </p>
                          </td>
                          <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                            <StatusBadge status={o.account_status} />
                            <p className="text-xs mt-1" style={{ color: d.color }}>{d.text}</p>
                          </td>
                          <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                            <span style={{ color: HOUSE.text, fontWeight: 700 }}>{o.counts.locations}</span>
                            <span className="text-xs" style={{ color: o.counts.hubs_online > 0 ? HOUSE.ok : HOUSE.dim }}> · {o.counts.hubs_online} online</span>
                            {o.paused.locations > 0 && <p className="text-xs" style={{ color: HOUSE.info }}>{o.paused.locations} paused by plan</p>}
                          </td>
                          <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                            <span style={{ color: HOUSE.text, fontWeight: 700 }}>{o.counts.members}</span>
                            {o.extra_members > 0 && <span className="text-xs" style={{ color: HOUSE.mute }}> · {o.extra_members} extra seat{o.extra_members === 1 ? '' : 's'}</span>}
                            {o.paused.members > 0 && <p className="text-xs" style={{ color: HOUSE.info }}>{o.paused.members} paused by plan</p>}
                          </td>
                          <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: HOUSE.text, fontWeight: 700 }}>{o.documents_this_month}</td>
                          <td style={{ padding: '10px 14px' }}><AttentionChips flags={o.attention} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {state === 'ok' && orgs.length > 0 && (
              <p className="text-xs mt-3" style={{ color: HOUSE.faint }}>{orgs.length} organization{orgs.length === 1 ? '' : 's'} · click a row to open it</p>
            )}
          </div>
        </>
      )}
    </PlatformAdminShell>
  );
}
